import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { Grid, SelectPicker, Whisper, Tooltip } from "rsuite";
import { toast } from "react-toastify";
import { PropertyStoreContext } from "../../../contexts/PropertyStoreContext";
import { searchRatePlans } from "../../../api";
import PropertyConfigHeader from "../PropertyConfigHeader";
import PropertyPageCard from "../PropertyPageCard";
import PropertyConfigTable from "../PropertyConfigTable";
import { getNextSortDirection } from "../tableUtils";
import { SORT_DIRECTIONS } from "../tableConstants";
import FilterIcon from "../../../assets/img/filter.svg?react";
import SummaryTooltipCell from "../../../components/common/SummaryTooltipCell";
import "../propertyConfig.scss";
import "./ratePlans.scss";

const PAGE_SIZE = 10;

const FILTER_OPTIONS = [
  { label: "All rate plans", value: "all" },
  { label: "Exclusive rate plans", value: "exclusive" },
  { label: "Non exclusive rate plans", value: "non_exclusive" },
];

const getColumns = (
  ratePlanSortDirection,
  statusSortDirection,
  onRatePlanSort,
  onStatusSort
) => [
  {
    key: "name",
    label: "Rate Plan",
    className: "rate-plans-page__col-rateplan",
    headerClassName: "rate-plans-page__col-rateplan-header",
    sortable: true,
    sortDirection: ratePlanSortDirection,
    onSort: onRatePlanSort,
    render: (row) => {
      let displayText = "-";
      if (row.code && row.name) {
        displayText = `${row.code} - ${row.name}`;
      } else {
        displayText = row.name || row.code || "-";
      }

      // Show tooltip only if text might be truncated
      if (displayText !== "-" && displayText.length > 30) {
        return (
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>{displayText}</Tooltip>}
          >
            <span className="rate-plans-page__truncated-text">
              {displayText}
            </span>
          </Whisper>
        );
      }
      return displayText;
    },
  },
  {
    key: "isActive",
    label: "Status",
    className: "rate-plans-page__col-status",
    headerClassName: "rate-plans-page__col-status-header",
    sortable: true,
    sortDirection: statusSortDirection,
    onSort: onStatusSort,
    render: (row) => (
      <span
        className={`rate-plans-page__status-badge${
          !row.isActive ? " rate-plans-page__status-badge--inactive" : ""
        }`}
      >
        {row.isActive ? "Active" : "Inactive"}
      </span>
    ),
  },
  {
    key: "exclusiveClients",
    label: "Exclusive Clients",
    className: "rate-plans-page__col-exclusive-clients",
    render: (row) => <SummaryTooltipCell items={row.exclusiveClients} />,
  },
  {
    key: "mealPlanName",
    label: "Mealplan",
    className: "rate-plans-page__col-mealplan",
    render: (row) => {
      const displayText = row.mealPlanName || "-";
      // Mealplan names might be longer, add tooltip for safety
      if (displayText !== "-" && displayText.length > 15) {
        return (
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>{displayText}</Tooltip>}
          >
            <span className="rate-plans-page__truncated-text">
              {displayText}
            </span>
          </Whisper>
        );
      }
      return displayText;
    },
  },
  {
    key: "mealPlanDescription",
    label: "Mealplan Description",
    className: "rate-plans-page__col-mealplan-desc",
    render: (row) => {
      const displayText = row.mealPlanDescription || "-";
      // Show tooltip for descriptions that might be truncated
      if (displayText !== "-" && displayText.length > 25) {
        return (
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>{displayText}</Tooltip>}
          >
            <span className="rate-plans-page__truncated-text">
              {displayText}
            </span>
          </Whisper>
        );
      }
      return displayText;
    },
  },
  {
    key: "policy",
    label: "Cancellation Policy",
    className: "rate-plans-page__col-cancellation",
    render: (row) => {
      const displayText = row?.policyName || row?.policyCode || "-";
      // Show tooltip for policy names that might be truncated
      if (displayText !== "-" && displayText.length > 20) {
        return (
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>{displayText}</Tooltip>}
          >
            <span className="rate-plans-page__truncated-text">
              {displayText}
            </span>
          </Whisper>
        );
      }
      return displayText;
    },
  },
];

const RatePlans = () => {
  const { selectedProperty } = useContext(PropertyStoreContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [exclusivityFilter, setExclusivityFilter] = useState("all");
  const [ratePlans, setRatePlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ratePlanSortDirection, setRatePlanSortDirection] = useState(null);
  const [statusSortDirection, setStatusSortDirection] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    size: PAGE_SIZE,
    totalElements: 0,
    totalPages: 0,
  });

  /**
   * Refs to prevent duplicate API calls during initial load.
   *
   * Problem: When the component mounts or property changes, the initial load effect
   * resets searchQuery to "" and exclusivityFilter to "all". These state changes
   * would normally trigger the search and filter effects, causing duplicate API calls.
   *
   * Solution: Use refs to track if we're in the initial load phase. The search and
   * filter effects check these refs and skip execution during initial load.
   *
   * We need separate refs because search and filter effects may trigger at different
   * times during the React render cycle.
   */
  const isInitialSearchRef = useRef(true);
  const isInitialFilterRef = useRef(true);

  const fetchRatePlans = useCallback(
    async (params = {}) => {
      if (!selectedProperty?.propertyId) return;

      setLoading(true);

      try {
        const sortBy = params.sortBy || "name";
        const sortOrder = params.sortOrder || "asc";

        const response = await searchRatePlans({
          propertyId: selectedProperty.propertyId,
          search: params.search || searchQuery,
          sortBy,
          sortOrder,
          page: params.page ?? pagination.page,
          size: params.size || PAGE_SIZE,
          clientType: params.clientType || exclusivityFilter,
        });

        const data = response?.data;
        if (data && Array.isArray(data.content)) {
          setRatePlans(data.content);
          setPagination({
            page: data.page ?? 0,
            size: data.size ?? PAGE_SIZE,
            totalElements: data.totalElements ?? 0,
            totalPages: data.totalPages ?? 0,
          });
        } else {
          toast.error("Failed to load rate plans. Please try again later.");
          setRatePlans([]);
        }
      } catch (error) {
        toast.error("Failed to load rate plans. Please try again later.");
        setRatePlans([]);
      } finally {
        setLoading(false);
      }
    },
    [
      selectedProperty?.propertyId,
      searchQuery,
      exclusivityFilter,
      pagination.page,
    ]
  );

  /**
   * Effect: Initial load and property change
   *
   * Triggers when:
   * - Component mounts for the first time
   * - User switches to a different property
   *
   * Actions:
   * 1. Sets both refs to true to signal initial load state
   * 2. Resets all filters and state to defaults
   * 3. Makes the initial API call with default parameters
   *
   * The refs prevent search/filter effects from making redundant calls
   * when state is reset to default values.
   */
  useEffect(() => {
    if (!selectedProperty?.propertyId) return;

    // Set refs to prevent search and filter effects from running
    isInitialSearchRef.current = true;
    isInitialFilterRef.current = true;

    // Reset all state to defaults
    setRatePlans([]);
    setSearchQuery("");
    setExclusivityFilter("all");
    setRatePlanSortDirection(null);
    setStatusSortDirection(null);
    setPagination({
      page: 0,
      size: PAGE_SIZE,
      totalElements: 0,
      totalPages: 0,
    });

    // Make the initial API call
    fetchRatePlans({ page: 0, search: "", clientType: "all" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty?.propertyId]);

  /**
   * Effect: Handle search query changes
   *
   * Triggers when: User types in the search input
   *
   * Features:
   * - Debounces API calls by 500ms to avoid excessive requests while typing
   * - Skips execution during initial load (when searchQuery is reset to "")
   * - Resets to page 0 when search changes
   *
   * The ref check prevents this effect from running when the initial load
   * effect resets searchQuery to "". Only subsequent user-initiated changes
   * will trigger the API call.
   */
  useEffect(() => {
    if (!selectedProperty?.propertyId) return;

    // Skip during initial load - the initial load effect handles the first call
    if (isInitialSearchRef.current) {
      isInitialSearchRef.current = false;
      return;
    }

    // Debounce search to avoid API calls on every keystroke
    const timeoutId = setTimeout(() => {
      fetchRatePlans({ page: 0, search: searchQuery });
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  /**
   * Effect: Handle filter changes
   *
   * Triggers when: User selects a different option in the filter dropdown
   *
   * Features:
   * - Fetches immediately (no debounce needed for dropdown selection)
   * - Skips execution during initial load (when filter is reset to "all")
   * - Resets to page 0 when filter changes
   *
   * The ref check prevents this effect from running when the initial load
   * effect resets exclusivityFilter to "all". Only subsequent user-initiated
   * changes will trigger the API call.
   */
  useEffect(() => {
    if (!selectedProperty?.propertyId) return;

    // Skip during initial load - the initial load effect handles the first call
    if (isInitialFilterRef.current) {
      isInitialFilterRef.current = false;
      return;
    }

    // Fetch immediately when filter changes (no debounce for dropdown)
    fetchRatePlans({ page: 0, clientType: exclusivityFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exclusivityFilter]);

  const handleRatePlanSortToggle = () => {
    const nextDirection = getNextSortDirection(ratePlanSortDirection);
    setRatePlanSortDirection(nextDirection);
    setStatusSortDirection(null);

    if (nextDirection) {
      const sortOrder = nextDirection === SORT_DIRECTIONS.ASC ? "asc" : "desc";
      fetchRatePlans({ page: 0, sortBy: "name", sortOrder });
    } else {
      fetchRatePlans({ page: 0, sortBy: "name", sortOrder: "asc" });
    }
  };

  const handleStatusSortToggle = () => {
    const nextDirection = getNextSortDirection(statusSortDirection);
    setStatusSortDirection(nextDirection);
    setRatePlanSortDirection(null);

    if (nextDirection) {
      const sortOrder = nextDirection === SORT_DIRECTIONS.ASC ? "asc" : "desc";
      fetchRatePlans({ page: 0, sortBy: "status", sortOrder });
    } else {
      fetchRatePlans({ page: 0, sortBy: "name", sortOrder: "asc" });
    }
  };

  const handlePageChange = (page) => {
    fetchRatePlans({ page });
  };

  return (
    <Grid fluid className="property-config-page">
      <PropertyConfigHeader title="Rate Plans" />

      <PropertyPageCard
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchAriaLabel="Search rate plans"
        toolbarLeft={
          <div className="rate-plans-page__filter-wrapper">
            <FilterIcon className="rate-plans-page__filter-icon" />
            <SelectPicker
              data={FILTER_OPTIONS}
              value={exclusivityFilter}
              onChange={setExclusivityFilter}
              cleanable={false}
              searchable={false}
              className="rate-plans-page__filter-select"
            />
          </div>
        }
      >
        <PropertyConfigTable
          columns={getColumns(
            ratePlanSortDirection,
            statusSortDirection,
            handleRatePlanSortToggle,
            handleStatusSortToggle
          )}
          data={ratePlans}
          rowKey="id"
          loading={loading}
          loadingMessage="Loading rate plans..."
          emptyMessage="No rate plans found."
          pagination="server"
          currentPage={pagination.page + 1}
          totalPages={pagination.totalPages}
          onPageChange={(page) => handlePageChange(page - 1)}
          pageSize={pagination.size}
        />
      </PropertyPageCard>
    </Grid>
  );
};

export default RatePlans;
